import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ProviderPageLayout from '../../components/ProviderPageLayout';
import { useAuth } from '../../auth/useAuth';
import {
  parseTransactionDate,
  formatDateTime,
  groupByMonth,
  filterByDateRange,
  filterByStatus,
} from '../../utils/csvParser';
import authApiService from '../../services/authApi';
import billingApi, { getPostLoginPath } from '../../services/billingApi';
import UsageDetailsPanel from '../../components/UsageDetailsPanel';
import { SupportContact } from '../../components/ui/SupportContact';
import { getTransactionUsageFee, isBillableTransaction, formatUsageMoney } from '../../config/billing';
import { cacheStoreInfo, getCachedStoreInfo } from '../../services/storeApi';
import { applyBillingProfile } from '../../utils/billingRegion';
import { extractFortuneViewCode, getFortuneViewUrl } from '../../utils/fortuneUrl';

function statusBadgeClass(status) {
  const s = (status || '').toLowerCase();
  if (['completed', 'generated', 'success', 'paid'].includes(s)) return 'provider-badge--completed';
  if (s === 'unaccepted') return 'provider-badge--unaccepted';
  if (s === 'pending') return 'provider-badge--pending';
  if (s === 'failed') return 'provider-badge--failed';
  return 'provider-badge--default';
}

function TransactionCodeLink({ transaction }) {
  const code =
    transaction.view_code || extractFortuneViewCode(transaction) || transaction.transaction_id;
  const url = transaction.fortune_url || getFortuneViewUrl(transaction);
  if (!code) return <span className="provider-table-mono">—</span>;
  if (!url) {
    return <span className="provider-table-mono">{code}</span>;
  }
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="provider-code-link"
      title="Open this reading on cupfate.com"
    >
      {code}
    </a>
  );
}

export const PAGE_SIZE = 20;

function Pager({ page, pageCount, total, onChange }) {
  if (pageCount <= 1) return null;
  const from = page * PAGE_SIZE + 1;
  const to = Math.min((page + 1) * PAGE_SIZE, total);
  return (
    <div className="provider-pager">
      <span className="provider-pager-info">
        {from}–{to} of {total}
      </span>
      <div className="provider-pager-controls">
        <button
          type="button"
          className="provider-btn provider-btn--ghost provider-btn--sm provider-btn--inline"
          onClick={() => onChange(page - 1)}
          disabled={page === 0}
        >
          ← Previous
        </button>
        <span className="provider-pager-page">
          Page {page + 1} of {pageCount}
        </span>
        <button
          type="button"
          className="provider-btn provider-btn--ghost provider-btn--sm provider-btn--inline"
          onClick={() => onChange(page + 1)}
          disabled={page >= pageCount - 1}
        >
          Next →
        </button>
      </div>
    </div>
  );
}

function TransactionRows({ transactions }) {
  return (
    <tbody>
      {transactions.map((txn, index) => (
        <tr key={txn.transaction_id || index}>
          <td>
            <TransactionCodeLink transaction={txn} />
          </td>
          <td>{txn.dateTime ? formatDateTime(txn.dateTime) : `${txn.date} ${txn.time}`}</td>
          <td>
            <span className={`provider-badge ${statusBadgeClass(txn.status)}`}>
              {txn.status || 'Unknown'}
            </span>
          </td>
        </tr>
      ))}
    </tbody>
  );
}

export function PaginatedTransactionTable({ transactions, tableClassName }) {
  const [page, setPage] = useState(0);
  const pageCount = Math.max(1, Math.ceil(transactions.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pageItems = transactions.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);

  useEffect(() => {
    setPage(0);
  }, [transactions]);

  return (
    <>
      <div className="provider-table-wrap">
        <table className={tableClassName}>
          <thead>
            <tr>
              <th>Code</th>
              <th>Date</th>
              <th>Status</th>
            </tr>
          </thead>
          <TransactionRows transactions={pageItems} />
        </table>
      </div>
      <Pager
        page={safePage}
        pageCount={pageCount}
        total={transactions.length}
        onChange={setPage}
      />
    </>
  );
}

function formatToday() {
  return new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function trialDaysLeft(isoString) {
  if (!isoString) return null;
  const ms = new Date(isoString).getTime() - Date.now();
  if (ms <= 0) return 0;
  return Math.ceil(ms / (24 * 60 * 60 * 1000));
}

const ProviderDashboard = () => {
  const [displayName, setDisplayName] = useState('');
  const [storeName, setStoreName] = useState('');
  const [storeCode, setStoreCode] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [billingProfile, setBillingProfile] = useState(null);
  const [usageSummary, setUsageSummary] = useState(null);

  const [selectedMonth, setSelectedMonth] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [viewMode, setViewMode] = useState('table');
  const [selectedMonthStatement, setSelectedMonthStatement] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);

  const navigate = useNavigate();
  const { user, loading: authLoading, signOut: cognitoSignOut } = useAuth();

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/provider/login');
      return;
    }

    getPostLoginPath().then((nextPath) => {
      if (nextPath === '/provider/payment-setup') navigate('/provider/payment-setup');
    });

    const attrs = user.attributes || {};
    const fullName = `${attrs.given_name || ''} ${attrs.family_name || ''}`.trim();
    setDisplayName(fullName);
    setStoreName(
      attrs['custom:store_name'] || getCachedStoreInfo()?.storeName || ''
    );
    setStoreCode(attrs['custom:device_id'] || sessionStorage.getItem('providerDeviceId') || '');
    setUserEmail(attrs.email || '');
    loadTransactions();
    loadUsageSummary();
  }, [user, authLoading, navigate]);

  const loadUsageSummary = async () => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) return;
      const data = await billingApi.getUsageSummary(accessToken);
      setUsageSummary(data);
    } catch {
      setUsageSummary(null);
    }
  };

  useEffect(() => {
    let filtered = [...transactions];

    if (selectedStatus !== 'all') {
      filtered = filterByStatus(filtered, selectedStatus);
    }

    if (startDate || endDate) {
      const start = startDate ? new Date(startDate) : null;
      const end = endDate ? new Date(endDate + 'T23:59:59') : null;
      filtered = filterByDateRange(filtered, start, end);
    }

    if (selectedMonth !== 'all') {
      filtered = filtered.filter((txn) => {
        if (!txn.dateTime) return false;
        const monthKey = `${txn.dateTime.getFullYear()}-${String(txn.dateTime.getMonth() + 1).padStart(2, '0')}`;
        return monthKey === selectedMonth;
      });
    }

    setFilteredTransactions(filtered);
  }, [transactions, selectedMonth, selectedStatus, startDate, endDate]);

  const loadTransactions = async () => {
    try {
      setLoading(true);
      setError('');
      const accessToken = localStorage.getItem('accessToken');
      const idToken = localStorage.getItem('idToken');
      if (!accessToken) throw new Error('Not authenticated. Please sign in again.');

      const result = await authApiService.getFortunes(accessToken, idToken);
      if (!result.success || !result.transactions) throw new Error('Failed to load transactions');

      if (result.billing) {
        applyBillingProfile(result.billing);
        setBillingProfile(result.billing);
      }

      // Store info lives on the device record; the API returns it here so the
      // welcome message works even for accounts missing custom:store_name.
      if (result.store) {
        cacheStoreInfo(result.store);
        if (result.store.storeName) setStoreName(result.store.storeName);
        if (result.store.deviceId) setStoreCode((code) => code || result.store.deviceId);
      }

      const processed = result.transactions
        .map((txn) => ({
          ...txn,
          dateTime: parseTransactionDate(txn.date, txn.time),
          usage_fee: typeof txn.usage_fee === 'number' ? txn.usage_fee : getTransactionUsageFee(txn),
          amount: parseFloat(txn.amount) || getTransactionUsageFee(txn),
        }))
        .sort((a, b) => {
          if (!a.dateTime || !b.dateTime) return 0;
          return b.dateTime - a.dateTime;
        });

      setTransactions(processed);
      setFilteredTransactions(processed);
    } catch (err) {
      setError(err.message || 'Failed to load transactions.');
      setTransactions([]);
      setFilteredTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await cognitoSignOut();
    } finally {
      sessionStorage.removeItem('providerUsername');
      sessionStorage.removeItem('providerDeviceId');
      sessionStorage.removeItem('providerProfile');
      navigate('/provider/login');
    }
  };

  const monthlyStatements = groupByMonth(transactions);

  const minuteGroups = useMemo(() => {
    if (viewMode !== 'minute') return {};
    const groups = {};
    filteredTransactions.forEach((txn) => {
      if (!txn.dateTime) return;
      const minuteKey = `${txn.dateTime.getFullYear()}-${String(txn.dateTime.getMonth() + 1).padStart(2, '0')}-${String(txn.dateTime.getDate()).padStart(2, '0')} ${String(txn.dateTime.getHours()).padStart(2, '0')}:${String(txn.dateTime.getMinutes()).padStart(2, '0')}`;
      if (!groups[minuteKey]) groups[minuteKey] = { minute: minuteKey, transactions: [] };
      groups[minuteKey].transactions.push(txn);
    });
    return groups;
  }, [filteredTransactions, viewMode]);

  const hasActiveFilters =
    selectedStatus !== 'all' || selectedMonth !== 'all' || Boolean(startDate) || Boolean(endDate);
  const completedCount = filteredTransactions.filter((t) => isBillableTransaction(t)).length;
  const unacceptedCount = filteredTransactions.filter((t) => !isBillableTransaction(t)).length;

  const getUsageDateRangeLabel = () => {
    if (startDate && endDate) return `${startDate} – ${endDate}`;
    if (startDate) return `from ${startDate}`;
    if (endDate) return `through ${endDate}`;
    if (selectedMonth !== 'all' && monthlyStatements[selectedMonth]) {
      return monthlyStatements[selectedMonth].month;
    }
    return 'All dates';
  };

  const clearFilters = () => {
    setSelectedMonth('all');
    setSelectedStatus('all');
    setStartDate('');
    setEndDate('');
    setSelectedMonthStatement('');
  };

  const firstName = displayName.split(' ')[0] || 'there';
  const trialPeriod = usageSummary?.currentPeriod;
  const showTrialCredit =
    trialPeriod?.hasTrial && trialPeriod?.trialActive && trialPeriod?.creditsRemaining > 0;
  const showTrialUsed =
    trialPeriod?.hasTrial && trialPeriod?.trialActive && trialPeriod?.creditsRemaining === 0;
  const trialCreditValue =
    showTrialCredit
      ? trialPeriod.creditsRemaining * (trialPeriod.unitAmount ?? 1)
      : 0;
  const trialIncludedValue =
    showTrialUsed
      ? (trialPeriod.trialCredits ?? 0) * (trialPeriod.unitAmount ?? 1)
      : 0;
  const trialDays = showTrialCredit ? trialDaysLeft(trialPeriod.trialEndsAt) : null;

  if (authLoading || loading) {
    return (
      <ProviderPageLayout variant="app" wide onSignOut={handleSignOut}>
        <div className="provider-dashboard-loading">
          <div className="provider-spinner" aria-hidden="true" />
          <span>{authLoading ? 'Checking session…' : 'Preparing your dashboard…'}</span>
        </div>
      </ProviderPageLayout>
    );
  }

  if (!user) return null;

  return (
    <ProviderPageLayout variant="app" wide onSignOut={handleSignOut}>
      <section className="provider-dashboard-hero">
        <div className="provider-dashboard-hero-copy">
          <p className="provider-dashboard-kicker">{formatToday()}</p>
          <h1 className="provider-dashboard-title">
            {storeName ? <>Welcome to {storeName}.</> : <>Welcome, {firstName}.</>}
          </h1>
          <p className="provider-dashboard-subtitle">
            {storeName
              ? 'Your readings, usage, and billing — all in one calm view.'
              : 'Your venue at a glance — usage, fortunes, and billing in one place.'}
          </p>
        </div>
        <UsageDetailsPanel
          transactions={filteredTransactions}
          billingProfile={billingProfile}
          dateRangeLabel={getUsageDateRangeLabel()}
          onSummary={setUsageSummary}
          initialSummary={usageSummary}
        />
      </section>

      <div className="provider-quick-actions">
        <Link to="/provider/billing" className="provider-quick-action">
          <span className="provider-quick-action-label">Billing</span>
          <span className="provider-quick-action-desc">Cards, invoices &amp; usage fees</span>
        </Link>
        <Link to="/provider/manual" className="provider-quick-action">
          <span className="provider-quick-action-label">Owner manual</span>
          <span className="provider-quick-action-desc">CupFate setup &amp; daily use</span>
        </Link>
        <div className="provider-quick-action provider-quick-action--support">
          <span className="provider-quick-action-label">Support</span>
          <span className="provider-quick-action-desc">Questions about your account or device</span>
          <SupportContact
            email={userEmail}
            storeName={storeName}
            storeCode={storeCode}
            triggerLabel="Email support"
            triggerClassName="provider-btn provider-btn--secondary provider-btn--sm provider-btn--inline"
          />
        </div>
      </div>

      {error && <div className="provider-alert provider-alert--error">{error}</div>}

      <div className="provider-stats provider-stats--dashboard">
        {showTrialCredit && (
          <div className="provider-stat provider-stat--credit">
            <div className="provider-stat-label">Trial credit</div>
            <div className="provider-stat-value provider-stat-value--success">
              {formatUsageMoney(trialCreditValue, trialPeriod.currency || billingProfile?.currency)}
            </div>
            <p className="provider-stat-foot">
              {trialDays === 1 ? '1 day left' : `${trialDays} days left`} · covers billable readings
            </p>
          </div>
        )}
        {showTrialUsed && (
          <div className="provider-stat provider-stat--credit provider-stat--credit-used">
            <div className="provider-stat-label">Trial credit</div>
            <div className="provider-stat-value">
              {formatUsageMoney(trialIncludedValue, trialPeriod.currency || billingProfile?.currency)}
            </div>
            <p className="provider-stat-foot">Included · fully applied to usage</p>
          </div>
        )}
        <div className="provider-stat">
          <div className="provider-stat-label">Transactions</div>
          <div className="provider-stat-value">{filteredTransactions.length}</div>
          <p className="provider-stat-foot">{hasActiveFilters ? 'Matching filters' : 'All time'}</p>
        </div>
        <div className="provider-stat">
          <div className="provider-stat-label">Completed</div>
          <div className="provider-stat-value provider-stat-value--success">{completedCount}</div>
          <p className="provider-stat-foot">Billable readings</p>
        </div>
        <div className="provider-stat">
          <div className="provider-stat-label">Unaccepted</div>
          <div className="provider-stat-value">{unacceptedCount}</div>
          <p className="provider-stat-foot">Not billable — no charge</p>
        </div>
      </div>

      <div className="provider-toolbar">
        <div className="provider-segmented" role="tablist" aria-label="View mode">
          {[
            ['table', 'All transactions'],
            ['monthly', 'By month'],
            ['minute', 'By minute'],
          ].map(([mode, label]) => (
            <button
              key={mode}
              type="button"
              role="tab"
              aria-selected={viewMode === mode}
              className={`provider-segment${viewMode === mode ? ' provider-segment--active' : ''}`}
              onClick={() => setViewMode(mode)}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="provider-toolbar-actions">
          <button
            type="button"
            className={`provider-btn provider-btn--ghost provider-btn--sm${filtersOpen ? ' provider-btn--active-ghost' : ''}`}
            onClick={() => setFiltersOpen((open) => !open)}
            aria-expanded={filtersOpen}
          >
            {filtersOpen ? 'Hide filters' : 'Filters'}
            {hasActiveFilters ? ' ·' : ''}
          </button>
          <button
            type="button"
            className="provider-btn provider-btn--ghost provider-btn--sm"
            onClick={() => {
              loadTransactions();
              loadUsageSummary();
            }}
          >
            Refresh
          </button>
        </div>
      </div>

      {filtersOpen && (
        <div className="provider-panel provider-panel--filters">
          <div className="provider-filters">
            <div className="provider-form-group" style={{ marginBottom: 0 }}>
              <label className="provider-label">Status</label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="provider-select"
              >
                <option value="all">All</option>
                <option value="completed">Completed</option>
                <option value="unaccepted">Unaccepted</option>
              </select>
            </div>
            <div className="provider-form-group" style={{ marginBottom: 0 }}>
              <label className="provider-label">Month</label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="provider-select"
              >
                <option value="all">All months</option>
                {Object.keys(monthlyStatements)
                  .sort()
                  .reverse()
                  .map((monthKey) => (
                    <option key={monthKey} value={monthKey}>
                      {monthlyStatements[monthKey].month}
                    </option>
                  ))}
              </select>
            </div>
            <div className="provider-form-group" style={{ marginBottom: 0 }}>
              <label className="provider-label">From</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="provider-input"
              />
            </div>
            <div className="provider-form-group" style={{ marginBottom: 0 }}>
              <label className="provider-label">To</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="provider-input"
              />
            </div>
          </div>
          {hasActiveFilters && (
            <button
              type="button"
              className="provider-btn provider-btn--ghost provider-btn--sm"
              onClick={clearFilters}
              style={{ marginTop: 16 }}
            >
              Clear filters
            </button>
          )}
        </div>
      )}

      {viewMode === 'table' && (
        <div className="provider-panel provider-panel--table">
          <div className="provider-panel-head">
            <h2 className="provider-panel-title">Transactions</h2>
            <p className="provider-panel-subtitle">
              {filteredTransactions.length} record{filteredTransactions.length !== 1 ? 's' : ''}
              {hasActiveFilters ? ' · filtered' : ''}
            </p>
          </div>
          {filteredTransactions.length === 0 ? (
            <div className="provider-empty">
              <p>No transactions match your filters.</p>
              {hasActiveFilters && (
                <button
                  type="button"
                  className="provider-btn provider-btn--secondary provider-btn--sm"
                  onClick={clearFilters}
                  style={{ marginTop: 16 }}
                >
                  Clear filters
                </button>
              )}
            </div>
          ) : (
            <PaginatedTransactionTable
              transactions={filteredTransactions}
              tableClassName="provider-table provider-table--dashboard provider-table--glass"
            />
          )}
        </div>
      )}

      {viewMode === 'monthly' && (
        <div className="provider-accordion-list">
          {Object.keys(monthlyStatements).length === 0 ? (
            <p className="provider-empty">No monthly statements yet.</p>
          ) : (
            Object.keys(monthlyStatements)
              .sort()
              .reverse()
              .map((monthKey) => {
                const statement = monthlyStatements[monthKey];
                const isOpen = selectedMonthStatement === monthKey;
                return (
                  <div
                    key={monthKey}
                    className={`provider-accordion${isOpen ? ' provider-accordion--open' : ''}`}
                  >
                    <div
                      className="provider-accordion-header"
                      onClick={() => setSelectedMonthStatement(isOpen ? '' : monthKey)}
                      onKeyDown={(e) =>
                        e.key === 'Enter' && setSelectedMonthStatement(isOpen ? '' : monthKey)
                      }
                      role="button"
                      tabIndex={0}
                    >
                      <div>
                        <h3 className="provider-accordion-title">{statement.month}</h3>
                        <p className="provider-accordion-meta">
                          {statement.transactions.length} transaction
                          {statement.transactions.length !== 1 ? 's' : ''}
                        </p>
                      </div>
                      <span className="provider-accordion-chevron" aria-hidden="true">
                        ▶
                      </span>
                    </div>
                    {isOpen && (
                      <div className="provider-accordion-body">
                        <PaginatedTransactionTable
                          transactions={statement.transactions}
                          tableClassName="provider-table provider-table--glass"
                        />
                      </div>
                    )}
                  </div>
                );
              })
          )}
        </div>
      )}

      {viewMode === 'minute' && (
        <div className="provider-minute-list">
          {Object.keys(minuteGroups).length === 0 ? (
            <p className="provider-empty">No transactions for the selected filters.</p>
          ) : (
            Object.keys(minuteGroups)
              .sort()
              .reverse()
              .map((minuteKey) => {
                const group = minuteGroups[minuteKey];
                return (
                  <div key={minuteKey} className="provider-panel">
                    <h3 className="provider-panel-title">{minuteKey}</h3>
                    <p className="provider-accordion-meta" style={{ marginBottom: 12 }}>
                      {group.transactions.length} transaction
                      {group.transactions.length !== 1 ? 's' : ''}
                    </p>
                    <ul className="provider-minute-items">
                      {group.transactions.map((txn, index) => (
                        <li key={txn.transaction_id || index} className="provider-minute-item">
                          <div>
                            <span className="provider-minute-id">
                              <TransactionCodeLink transaction={txn} />
                            </span>
                          </div>
                          <span className={`provider-badge ${statusBadgeClass(txn.status)}`}>
                            {txn.status}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })
          )}
        </div>
      )}
    </ProviderPageLayout>
  );
};

export default ProviderDashboard;
